import {
  faArrowsRotate,
  faBoxCheck,
  faChevronRight,
  faClock,
  faComments,
  faEnvelope,
  faFaceSmile,
  faShieldCheck,
  faUserPlus,
} from '@fortawesome/pro-regular-svg-icons';
import NumberFlow, { continuous } from '@number-flow/react';
import {
  Card,
  ChartContainer,
  ChartGranularity,
  ChartTooltip,
  CorsoIcon,
  datePresetOptions,
  Icon,
} from 'corso-admin-ui';
import { formatter } from 'corso-web-lib';
import { format } from 'date-fns';
import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Line,
  LineChart,
  ResponsiveContainer,
} from 'recharts';
import { useEnabledClaimType } from '~/hooks/useEnabledClaimType';
import { useStoreId, useStoreUser } from '~/providers/StoreUserProvider';
import { reportGroups } from '../reports/groups';
import { ChangeBadge } from './ChangeBadge';
import {
  changeOf,
  ImpactKind,
  sumOf,
  valueStreams,
  type DashboardSection,
  type ValueHomeData,
} from './valueHome';

const whole = (value: number) =>
  formatter.number(value, { maximumFractionDigits: 0 });

// a null section is the Customer Success report, which sits outside the Dashboards page
const impactCopy = {
  [ImpactKind.customersKept]: {
    label: 'Returns saved',
    description: 'Kept from ending in a refund',
    icon: faArrowsRotate,
    format: whole,
    section: { group: 'returns', report: 'overview' },
  },
  [ImpactKind.ordersMadeRight]: {
    label: 'Orders made right',
    description: 'Lost, stolen, or damaged orders reordered or refunded',
    icon: faBoxCheck,
    format: whole,
    section: { group: 'shippingProtection', report: 'claims' },
  },
  [ImpactKind.hoursSaved]: {
    label: 'Hours given back',
    description: 'Estimated from orders customers edited themselves',
    icon: faClock,
    format: whole,
    section: { group: 'orderEditing', report: 'overview' },
  },
  [ImpactKind.conversations]: {
    label: 'Support conversations',
    description: 'Customer conversations Corso handled for you',
    icon: faComments,
    format: whole,
    section: null,
  },
  [ImpactKind.satisfaction]: {
    label: 'Customer satisfaction',
    description: 'Of customers who rated their support were happy',
    icon: faFaceSmile,
    format: (score: number) => `${whole(score)}%`,
    section: null,
  },
  [ImpactKind.newCustomers]: {
    label: 'New customers',
    description: 'Created in your store by product registrations',
    icon: faUserPlus,
    format: whole,
    section: { group: 'registrations', report: 'overview' },
  },
  [ImpactKind.newSubscribers]: {
    label: 'New email subscribers',
    description: 'Subscribed through product registrations',
    icon: faEnvelope,
    format: whole,
    section: { group: 'registrations', report: 'overview' },
  },
  [ImpactKind.warrantiesServiced]: {
    label: 'Warranties serviced',
    description: 'Warranty claims resolved for your customers',
    icon: faShieldCheck,
    format: whole,
    section: { group: 'warranties', report: 'overview' },
  },
} satisfies Record<
  ImpactKind,
  {
    label: string;
    description: string;
    icon: typeof faShieldCheck;
    format: (amount: number) => string;
    section: DashboardSection | null;
  }
>;

// the features that earn revenue, offered on the home when they're off
const offers = [
  {
    group: 'returns',
    label: 'Returns',
    pitch:
      'Keep refunds in your store with exchanges, store credit, and Shop Now',
    settings: 'returns',
  },
  {
    group: 'orderEditing',
    label: 'Order edits',
    pitch: 'Let customers add to their own orders after checkout',
    settings: 'order-editing',
  },
  {
    group: 'shippingProtection',
    label: 'Checkout Plus',
    pitch: 'Earn on every order protected at checkout',
    settings: 'checkout-plus',
  },
] as const satisfies {
  group: DashboardSection['group'];
  label: string;
  pitch: string;
  settings: string;
}[];

// whole weeks counted back from the last day, so the newest week is never partial
const weeksOf = (days: readonly string[], daily: readonly number[]) => {
  const offset = daily.length % 7;
  return Array.from({ length: Math.floor(daily.length / 7) }, (_, week) => {
    const start = offset + week * 7;
    return {
      start: days[start] ?? '',
      amount: sumOf(daily.slice(start, start + 7)),
    };
  });
};

// a null section is the Customer Success report
const productOf = (section: DashboardSection | null) =>
  section
    ? (reportGroups.find(({ key }) => key === section.group)?.label ?? '')
    : 'Customer Success';

/** The home of what Corso does for the store: the period's total up front, then where it came from and everything beyond revenue. */
export const ValueOverview = ({ data }: { data: ValueHomeData }) => {
  const { store } = useStoreUser();
  const storeId = useStoreId();
  const enabled = useEnabledClaimType();
  const gradientId = useId();

  const net = sumOf(data.streams.map(({ value }) => value));
  const previous = sumOf(data.streams.map((stream) => stream.previous));
  // NumberFlow animates a change of value, so the total starts at zero and counts up once mounted
  const [shown, setShown] = useState(0);
  useEffect(() => setShown(net), [net]);

  const money = (amount: number) =>
    formatter.currency(amount, store.currencyCode, false, {
      maximumFractionDigits: 0,
    });
  const linkTo = (section: DashboardSection | null) =>
    section
      ? `/${storeId}/analytics/dashboards/${section.group}?${new URLSearchParams(
          { report: section.report, range: data.preset },
        ).toString()}`
      : `/${storeId}/analytics/customer-success`;

  const periodLabel = (
    datePresetOptions.find(({ value }) => value === data.preset)?.label ?? ''
  ).toLowerCase();
  // a month or less reads day by day, a quarter week by week, a year month by month
  const isWeekly =
    data.granularity === ChartGranularity.day && data.days.length > 31;
  // the newest days stay empty until their invoice arrives, so a line ends at its last value rather than dropping to zero
  const pointsOf = (amounts: readonly number[]) => {
    const all = isWeekly
      ? weeksOf(data.days, amounts)
      : data.days.map((start, index) => ({
          start,
          amount: amounts[index] ?? 0,
        }));
    const end = all.reduce(
      (last, { amount }, index) => (amount === 0 ? last : index + 1),
      0,
    );
    return all.slice(0, end);
  };
  const points = pointsOf(
    data.days.map((_, index) =>
      sumOf(data.streams.map(({ daily }) => daily[index] ?? 0)),
    ),
  );
  const pointLabel = (start: string) => {
    const date = new Date(`${start}T00:00:00`);
    return data.granularity === ChartGranularity.month
      ? format(date, 'MMM yyyy')
      : `${isWeekly ? 'Week of ' : ''}${format(date, 'MMM d')}`;
  };
  const notUsed = offers.filter(
    (offer) =>
      !reportGroups.find(({ key }) => key === offer.group)?.isEnabled(enabled),
  );
  const roi = data.planCost
    ? `${formatter.number(net / data.planCost, { maximumFractionDigits: 1 })}×`
    : null;

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="flex flex-col gap-3">
            <Card.Heading as="h2">Corso made you</Card.Heading>
            <NumberFlow
              value={shown}
              format={{
                style: 'currency',
                currency: store.currencyCode,
                maximumFractionDigits: 0,
              }}
              plugins={[continuous]}
              className="text-5xl leading-none font-semibold tracking-tight text-neutral-900 tabular-nums"
            />
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-neutral-600">
                in the {periodLabel}
              </span>
              <ChangeBadge change={changeOf(net, previous)} />
            </div>
          </div>
          {roi && (
            <span
              aria-label={`${roi} ROI on Corso`}
              className="mr-2 grid size-32 shrink-0 -rotate-12 place-items-center rounded-full border-2 border-corso-blue-600 p-1 text-corso-blue-600"
            >
              <span
                aria-hidden
                className="flex size-full flex-col items-center justify-center gap-1 rounded-full border border-dashed border-current"
              >
                {/* Corso's mark turned on its side is Carl, the smile */}
                <span className="-rotate-90">
                  <CorsoIcon />
                </span>
                <span className="text-3xl leading-none font-bold tracking-tight tabular-nums">
                  {roi}
                </span>
                <span className="text-[10px] font-semibold tracking-widest uppercase">
                  ROI
                </span>
              </span>
            </span>
          )}
        </div>
        {points.some(({ amount }) => amount !== 0) && (
          <ChartContainer className="h-40">
            <AreaChart
              data={points}
              margin={{ top: 8, right: 0, bottom: 0, left: 0 }}
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor="var(--color-corso-blue-600)"
                    stopOpacity={0.22}
                  />
                  <stop
                    offset="100%"
                    stopColor="var(--color-corso-blue-600)"
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <ChartTooltip
                cursor={{ stroke: 'var(--color-neutral-200)' }}
                formatLabel={pointLabel}
                formatValue={money}
              />
              <Area
                type="monotone"
                dataKey="amount"
                name="Corso made you"
                stroke="var(--color-corso-blue-600)"
                strokeWidth={2.5}
                fill={`url(#${gradientId})`}
              />
            </AreaChart>
          </ChartContainer>
        )}
      </Card>

      <Card flush>
        <div className="px-6 pt-5 pb-2">
          <Card.Heading as="h2">Where it came from</Card.Heading>
        </div>
        <ul>
          {data.streams.map(({ key, value, previous: before, daily }) => (
            <li key={key}>
              <Link
                to={linkTo(valueStreams[key].section)}
                className="flex flex-wrap items-center gap-4 border-t border-neutral-100 px-6 py-4 text-neutral-900 hover:bg-neutral-50"
              >
                <span className="min-w-0 flex-[1_1_220px]">
                  <span className="block text-sm font-semibold">
                    {valueStreams[key].label}
                  </span>
                  <span className="block text-xs text-neutral-500">
                    {valueStreams[key].description}
                  </span>
                </span>
                <span className="hidden h-8 w-40 sm:block" aria-hidden>
                  {daily.some((amount) => amount !== 0) && (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        accessibilityLayer={false}
                        data={pointsOf(daily)}
                        margin={{ top: 2, right: 0, bottom: 2, left: 0 }}
                      >
                        <Line
                          type="monotone"
                          dataKey="amount"
                          stroke="var(--color-corso-blue-600)"
                          strokeWidth={2}
                          dot={false}
                          isAnimationActive={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </span>
                <span className="flex w-20 justify-end">
                  <ChangeBadge change={changeOf(value, before)} />
                </span>
                <span className="w-36 text-right text-xl font-semibold tracking-tight tabular-nums">
                  {money(value)}
                </span>
                <Icon
                  icon={faChevronRight}
                  className="size-3.5 text-neutral-400"
                />
              </Link>
            </li>
          ))}
          {notUsed.map(({ group, label, pitch, settings }) => (
            <li key={group}>
              <Link
                to={`/${storeId}/settings/${settings}`}
                className="flex flex-wrap items-center gap-4 border-t border-neutral-100 px-6 py-4 text-neutral-900 hover:bg-corso-blue-50/60"
              >
                <span className="min-w-0 flex-[1_1_220px]">
                  <span className="block text-sm font-semibold">{label}</span>
                  <span className="block text-xs text-neutral-500">
                    {pitch}
                  </span>
                </span>
                <span className="text-xs font-medium text-corso-blue-700">
                  Turn on
                </span>
                <span className="w-36 text-right text-xl font-semibold tracking-tight text-neutral-300">
                  <span aria-hidden>
                    {formatter.currencySymbol(store.currencyCode)}?
                  </span>
                  <span className="sr-only">Not turned on yet</span>
                </span>
                <Icon
                  icon={faChevronRight}
                  className="size-3.5 text-neutral-400"
                />
              </Link>
            </li>
          ))}
        </ul>
      </Card>

      {data.impacts.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="text-base font-semibold text-neutral-900">
            Beyond revenue
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {data.impacts.map(({ kind, value }) => {
              const copy = impactCopy[kind];
              return (
                <Link
                  key={kind}
                  to={linkTo(copy.section)}
                  className="hover:border-corso-blue-200 flex flex-col gap-2.5 rounded-lg border bg-white p-4 text-neutral-900 shadow-sm transition-colors"
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="grid size-9 place-items-center rounded-lg bg-corso-blue-50 text-corso-blue-600">
                      <Icon icon={copy.icon} className="size-4" />
                    </span>
                    <span className="text-xs text-neutral-500">
                      {productOf(copy.section)}
                    </span>
                  </span>
                  <span className="text-3xl leading-none font-semibold tracking-tight tabular-nums">
                    {copy.format(value)}
                  </span>
                  <span className="text-sm font-semibold">{copy.label}</span>
                  <span className="text-xs text-neutral-500">
                    {copy.description}
                  </span>
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};
